import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Play, FileText, ArrowRight, AlertTriangle, Share2, X } from 'lucide-react';
import { useKiprData } from '@/lib/kiprData';
import { supabase } from '@/lib/supabase';
import type { KipReport as KipReportModel } from '@/types';

function speakerLabel(role: string): string {
  if (role === 'assistant') return 'AI Persona';
  if (role === 'user') return 'Therapist';
  return 'System';
}

function demoTranscriptFromHighlights(report: KipReportModel): string {
  if (report.transcriptHighlights.length === 0) {
    return 'No sample lines are available for this demo report.';
  }
  return report.transcriptHighlights
    .map((h) => `${h.speaker}: "${h.text}"\n[${h.note}]`)
    .join('\n\n');
}

type SessionArtifacts = {
  loading: boolean;
  error: string | null;
  audioUrl: string | null;
  transcript: string | null;
  transcriptTurns: Array<{ role: string; content: string }>;
  audioStatus: 'unavailable' | 'processing' | 'ready' | 'failed';
  transcriptStatus: 'unavailable' | 'processing' | 'ready' | 'failed';
};

const initialArtifacts: SessionArtifacts = {
  loading: false,
  error: null,
  audioUrl: null,
  transcript: null,
  transcriptTurns: [],
  audioStatus: 'unavailable',
  transcriptStatus: 'unavailable',
};

function parseTranscriptTurns(transcript: string): Array<{ role: string; content: string }> {
  return transcript
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^([^:]{2,40}):\s*(.+)$/);
      if (!match) return { role: 'system', content: line };
      const rawSpeaker = match[1].trim().toLowerCase();
      const content = match[2].trim();
      if (rawSpeaker.includes('therapist') || rawSpeaker === 'user') return { role: 'user', content };
      if (rawSpeaker.includes('ai') || rawSpeaker.includes('assistant') || rawSpeaker.includes('persona')) {
        return { role: 'assistant', content };
      }
      return { role: 'system', content };
    });
}

function availabilityLabel(status: SessionArtifacts['audioStatus']): string {
  if (status === 'ready') return 'Ready';
  if (status === 'processing') return 'Processing';
  if (status === 'failed') return 'Failed';
  return 'Unavailable';
}

const KipReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { kips, reports, loading } = useKiprData();

  const report = reports.find((r) => r.id === id) ?? reports.find((r) => r.kipId === id);
  const kip = kips.find((k) => k.id === report?.kipId) ?? kips.find((k) => k.id === id);

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [listenOpen, setListenOpen] = useState(false);
  const [sessionArtifacts, setSessionArtifacts] = useState<SessionArtifacts>(initialArtifacts);

  const fetchSessionArtifacts = useCallback(async (): Promise<{ shouldPoll: boolean }> => {
    if (!report?.sessionId || !supabase) {
      setSessionArtifacts(initialArtifacts);
      return { shouldPoll: false };
    }

    setSessionArtifacts((s) => ({ ...s, loading: true, error: null }));

    try {
      const [{ data: rec, error: recErr }, { data: session, error: sessionErr }] = await Promise.all([
        supabase
          .from('session_recordings')
          .select('audio_storage_path, transcription_text, status, error_message')
          .eq('session_id', report.sessionId)
          .maybeSingle(),
        supabase.from('kip_sessions').select('status').eq('id', report.sessionId).maybeSingle(),
      ]);

      if (recErr) throw recErr;
      if (sessionErr) throw sessionErr;

      let audioUrl: string | null = null;
      if (rec?.audio_storage_path) {
        const { data: signed, error: signErr } = await supabase.storage
          .from('session-audio')
          .createSignedUrl(rec.audio_storage_path, 3600);
        if (signErr) {
          console.warn(signErr);
        } else {
          audioUrl = signed?.signedUrl ?? null;
        }
      }

      const recordingStatus = rec?.status ?? null;
      const sessionStatus = session?.status ?? null;
      const recordingFailed = recordingStatus === 'failed' || sessionStatus === 'failed';
      const transcriptFromRecording = rec?.transcription_text?.trim() || null;

      let transcriptTurns: Array<{ role: string; content: string }> = [];
      let transcript = transcriptFromRecording;
      if (!transcript) {
        const { data: turns, error: turnsErr } = await supabase
          .from('session_turns')
          .select('role, content, turn_index')
          .eq('session_id', report.sessionId)
          .order('turn_index', { ascending: true });

        if (turnsErr) throw turnsErr;
        if (turns?.length) {
          transcriptTurns = turns.map((t) => ({ role: t.role, content: t.content }));
          transcript = turns.map((t) => `${speakerLabel(t.role)}: ${t.content}`).join('\n');
        }
      } else {
        transcriptTurns = parseTranscriptTurns(transcript);
      }

      const audioStatus: SessionArtifacts['audioStatus'] = recordingFailed
        ? 'failed'
        : audioUrl
          ? 'ready'
          : recordingStatus === 'pending' || recordingStatus === 'audio_persisted'
            ? 'processing'
            : !recordingStatus && (sessionStatus === 'completed' || sessionStatus === 'report_ready')
              ? 'processing'
              : 'unavailable';

      const transcriptStatus: SessionArtifacts['transcriptStatus'] = recordingFailed
        ? 'failed'
        : transcript
          ? 'ready'
          : recordingStatus === 'pending' || recordingStatus === 'audio_persisted'
            ? 'processing'
            : !recordingStatus && (sessionStatus === 'completed' || sessionStatus === 'report_ready')
              ? 'processing'
              : 'unavailable';

      const error = rec?.error_message?.trim() || null;
      setSessionArtifacts({
        loading: false,
        error,
        audioUrl,
        transcript,
        transcriptTurns,
        audioStatus,
        transcriptStatus,
      });

      return { shouldPoll: audioStatus === 'processing' || transcriptStatus === 'processing' };
    } catch (e) {
      setSessionArtifacts({
        loading: false,
        error: e instanceof Error ? e.message : 'Unable to load session data',
        audioUrl: null,
        transcript: null,
        transcriptTurns: [],
        audioStatus: 'failed',
        transcriptStatus: 'failed',
      });
      return { shouldPoll: false };
    }
  }, [report?.sessionId, supabase]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const run = async () => {
      const { shouldPoll } = await fetchSessionArtifacts();
      if (cancelled || !shouldPoll) return;
      timeoutId = window.setTimeout(() => {
        void run();
      }, 4000);
    };

    void run();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [fetchSessionArtifacts]);

  const closeModals = useCallback(() => {
    setTranscriptOpen(false);
    setListenOpen(false);
  }, []);

  useEffect(() => {
    if (!transcriptOpen && !listenOpen) return;

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeModals();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [transcriptOpen, listenOpen, closeModals]);

  if (loading) {
    return <div className="text-[#555555]">Loading report...</div>;
  }

  if (!kip || !report) {
    return <div>Report not found</div>;
  }

  const renderStars = (score: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < score ? 'fill-yellow-400 text-yellow-400' : 'text-[#DADADA]'}
      />
    ));
  };

  const resolveTranscriptBody = (): { title: string; body: string; isDemo: boolean } => {
    if (report.sessionId) {
      if (sessionArtifacts.loading) {
        return { title: 'Call transcript', body: 'Loading transcript…', isDemo: false };
      }
      if (sessionArtifacts.error) {
        return { title: 'Call transcript', body: sessionArtifacts.error, isDemo: false };
      }
      if (sessionArtifacts.transcriptStatus === 'processing') {
        return {
          title: 'Call transcript',
          body: 'Transcript is still processing. This page updates automatically when it becomes available.',
          isDemo: false,
        };
      }
      if (sessionArtifacts.transcript) {
        return { title: 'Call transcript', body: sessionArtifacts.transcript, isDemo: false };
      }
      return {
        title: 'Call transcript',
        body: 'No transcript was found for this session yet.',
        isDemo: false,
      };
    }

    return {
      title: 'Transcript excerpts (demo)',
      body: demoTranscriptFromHighlights(report),
      isDemo: true,
    };
  };

  const transcriptResolved = resolveTranscriptBody();
  const transcriptButtonLabel =
    sessionArtifacts.transcriptStatus === 'processing'
      ? 'View Transcript (Processing...)'
      : sessionArtifacts.transcriptStatus === 'failed'
        ? 'View Transcript (Unavailable)'
        : 'View Transcript';

  const renderListenBody = () => {
    if (!report.sessionId) {
      return (
        <p className="text-[#555555] text-sm leading-relaxed">
          Call recordings appear here after you complete a live training session that generates this report. Demo and
          seed reports do not include audio.
        </p>
      );
    }
    if (sessionArtifacts.loading && sessionArtifacts.audioStatus !== 'ready') {
      return <p className="text-[#555555] text-sm">Loading recording…</p>;
    }
    if (sessionArtifacts.error) {
      return <p className="text-red-700 text-sm">{sessionArtifacts.error}</p>;
    }
    if (sessionArtifacts.audioStatus === 'processing') {
      return (
        <p className="text-[#555555] text-sm leading-relaxed">
          Audio is still processing. This page updates automatically once the recording is available.
        </p>
      );
    }
    if (sessionArtifacts.audioUrl) {
      return <audio controls className="w-full mt-2" src={sessionArtifacts.audioUrl} />;
    }
    return (
      <p className="text-[#555555] text-sm leading-relaxed">
        No recording file was found for this session. Audio is saved when the post-call webhook delivers call audio to
        Kipr.
      </p>
    );
  };

  const scoreAreas = [
    { label: 'Communication Skills', score: report.scores.communicationSkills, note: report.scoreNotes.communicationSkills },
    { label: 'Bedside Manner', score: report.scores.bedsideManner, note: report.scoreNotes.bedsideManner },
    { label: 'Clinical Accuracy', score: report.scores.clinicalAccuracy, note: report.scoreNotes.clinicalAccuracy },
    { label: 'Goal Achievement', score: report.scores.goalAchievement, note: report.scoreNotes.goalAchievement },
    { label: 'ABA Technical Skills', score: report.scores.abaSkills, note: report.scoreNotes.abaSkills },
  ];

  return (
    <div className="space-y-8">
      {transcriptOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kipr-transcript-title"
          onClick={closeModals}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 id="kipr-transcript-title" className="text-lg font-semibold text-[#1A1A1A] font-poppins">
                {transcriptResolved.title}
              </h2>
              <button
                type="button"
                onClick={closeModals}
                className="p-2 rounded-md text-[#555555] hover:bg-gray-100"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {transcriptResolved.isDemo && (
                <p className="text-sm text-[#555555] mb-4 border-l-4 border-amber-400 bg-amber-50 pl-3 py-2">
                  This is a demo report without a stored session transcript. Showing highlights-style excerpts below.
                </p>
              )}
              {sessionArtifacts.transcriptTurns.length > 0 && !transcriptResolved.isDemo ? (
                <div className="space-y-3">
                  {sessionArtifacts.transcriptTurns.map((turn, index) => (
                    <div key={`${turn.role}-${index}`} className="rounded-lg border border-gray-200 p-3 bg-[#FAFAFA]">
                      <div className="mb-1">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            turn.role === 'assistant'
                              ? 'bg-violet-100 text-violet-800'
                              : turn.role === 'user'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {speakerLabel(turn.role)}
                        </span>
                      </div>
                      <p className="text-sm text-[#1A1A1A] leading-relaxed">{turn.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <pre className="text-sm text-[#1A1A1A] whitespace-pre-wrap font-sans leading-relaxed">
                  {transcriptResolved.body}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {listenOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kipr-listen-title"
          onClick={closeModals}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="kipr-listen-title" className="text-lg font-semibold text-[#1A1A1A] font-poppins">
                Listen to call
              </h2>
              <button
                type="button"
                onClick={closeModals}
                className="p-2 rounded-md text-[#555555] hover:bg-gray-100 -mr-2"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            {renderListenBody()}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="mb-4">
          <div className="text-sm text-[#555555] font-medium mb-1">Kip Report</div>
          <h1 className="text-2xl sm:text-3xl font-bold font-poppins text-[#1A1A1A] mb-2">
            {kip.title}
          </h1>

          {/* Action Buttons Row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4">
            <button
              type="button"
              className="flex items-center justify-center sm:justify-start space-x-2 text-[#121212] hover:text-[#555555] transition-colors px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-200"
            >
              <Share2 size={18} />
              <span className="font-medium text-sm">Share Report</span>
            </button>
            <button
              type="button"
              onClick={() => setListenOpen(true)}
              className="flex items-center justify-center sm:justify-start space-x-2 text-[#121212] hover:text-[#555555] transition-colors px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-200"
            >
              <Play size={18} />
              <span className="font-medium text-sm">Listen to Call</span>
            </button>
            <button
              type="button"
              onClick={() => setTranscriptOpen(true)}
              className="flex items-center justify-center sm:justify-start space-x-2 text-[#121212] hover:text-[#555555] transition-colors px-4 py-2 rounded-lg hover:bg-gray-50 border border-gray-200"
            >
              <FileText size={18} />
              <span className="font-medium text-sm">{transcriptButtonLabel}</span>
            </button>
          </div>
          {report.sessionId && (
            <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-[#555555]">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
                <span className="font-medium">Audio:</span> {availabilityLabel(sessionArtifacts.audioStatus)}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
                <span className="font-medium">Transcript:</span> {availabilityLabel(sessionArtifacts.transcriptStatus)}
              </span>
            </div>
          )}

          {/* Report Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-[#555555]">
            <p><strong>Therapist:</strong> {report.therapist}</p>
            <p><strong>Guardian:</strong> {report.simulatedParent}</p>
            <p><strong>Client:</strong> {report.client}</p>
            <p><strong>Date:</strong> {report.date}</p>
            <p><strong>Time:</strong> {report.time}</p>
            <p><strong>Duration:</strong> {report.duration}</p>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-6">📊 Summary of Performance</h2>
        <div className="space-y-4">
          {scoreAreas.map((area, index) => (
            <div key={index} className="border border-[#DADADA] rounded-lg p-4 bg-[#FAFAFA]">
              <div className="font-semibold text-[#1A1A1A] text-lg mb-2">
                {area.label}
              </div>
              <div className="flex items-center space-x-1 mb-3">
                <span className="text-sm text-[#555555] mr-2">Score:</span>
                {renderStars(area.score)}
                <span className="text-sm text-[#555555] ml-2">({area.score}/5)</span>
              </div>
              <div className="text-[#555555] text-sm leading-relaxed">
                <span className="font-medium">Notes:</span> {area.note}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Feedback */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-6">🧠 Detailed Feedback</h2>

        <div className="space-y-6">
          {/* Communication Skills */}
          <div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">
              🗣️ Communication Skills – {report.feedback.communicationSkills.score}/5
            </h3>
            <ul className="space-y-2 mb-4">
              {report.feedback.communicationSkills.details.map((detail, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-[#121212] mr-2">•</span>
                  <span dangerouslySetInnerHTML={{ __html: detail.replace(/(\w+:)/, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <p className="text-green-800">{report.feedback.communicationSkills.highlight}</p>
            </div>
          </div>

          {/* Bedside Manner */}
          <div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">
              💬 Bedside Manner – {report.feedback.bedsideManner.score}/5
            </h3>
            <ul className="space-y-2 mb-4">
              {report.feedback.bedsideManner.details.map((detail, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-[#121212] mr-2">•</span>
                  <span dangerouslySetInnerHTML={{ __html: detail.replace(/(\w+:)/, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
              <p className="text-green-800">{report.feedback.bedsideManner.highlight}</p>
            </div>
          </div>

          {/* Clinical Accuracy */}
          <div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">
              📚 Clinical Accuracy – {report.feedback.clinicalAccuracy.score}/5
            </h3>
            <ul className="space-y-2 mb-4">
              {report.feedback.clinicalAccuracy.details.map((detail, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-[#121212] mr-2">•</span>
                  <span dangerouslySetInnerHTML={{ __html: detail.replace(/(\w+:)/, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-amber-800 flex items-start">
                <AlertTriangle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Tip:</strong> {report.feedback.clinicalAccuracy.warning}</span>
              </p>
            </div>
          </div>

          {/* Goal Achievement */}
          <div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">
              🎯 Goal Achievement – {report.feedback.goalAchievement.score}/5
            </h3>
            <ul className="space-y-2">
              {report.feedback.goalAchievement.details.map((detail, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-[#121212] mr-2">•</span>
                  <span dangerouslySetInnerHTML={{ __html: detail.replace(/(\w+:)/, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>
          </div>

          {/* ABA Technical Skills */}
          <div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">
              🧠 ABA Technical Skills – {report.feedback.abaSkills.score}/5
            </h3>
            <ul className="space-y-2">
              {report.feedback.abaSkills.details.map((detail, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-[#121212] mr-2">•</span>
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Transcript Highlights */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-6">✏️ Transcript Highlights</h2>
        <div className="space-y-4">
          {report.transcriptHighlights.map((highlight, index) => (
            <div
              key={index}
              className={`p-4 rounded border-l-4 ${
                highlight.type === 'correct'
                  ? 'bg-green-50 border-green-500'
                  : 'bg-amber-50 border-amber-500'
              }`}
            >
              <p className={`font-medium ${
                highlight.type === 'correct' ? 'text-green-800' : 'text-amber-800'
              }`}>
                <strong>{highlight.speaker}:</strong> &quot;{highlight.text}&quot;
              </p>
              <p className={`text-sm mt-1 italic ${
                highlight.type === 'correct' ? 'text-green-700' : 'text-amber-700'
              }`}>
                [{highlight.note}]
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-6">✅ Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-[#1A1A1A] mb-3">Strengths:</h3>
            <ul className="space-y-2">
              {report.summary.strengths.map((strength, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-[#1A1A1A] mb-3">Growth Areas:</h3>
            <ul className="space-y-2">
              {report.summary.growthAreas.map((area, index) => (
                <li key={index} className="text-[#555555] flex items-start">
                  <span className="text-amber-600 mr-2">!</span>
                  {area}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Suggested Follow-Up */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold font-poppins text-[#1A1A1A] mb-6">📚 Suggested Follow-Up</h2>
        <ul className="space-y-3">
          {report.suggestedFollowUp.map((item, index) => (
            <li key={index} className="text-[#555555] flex items-start">
              <span className="text-[#121212] mr-2">•</span>
              <span>
                <strong>{item.type}:</strong> &quot;{item.title}&quot;
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Next Kip Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => navigate('/personas')}
          className="bg-[#121212] text-white px-8 py-4 rounded-md shadow-lg font-semibold font-inter hover:bg-[#2A2A2A] transition-colors flex items-center space-x-2 mx-auto"
        >
          <span>Continue Training</span>
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default KipReport;
